import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import {
  FlightCheckbox,
  FlightSelect,
  FlightTable,
  FlightTabPanel,
  FlightTabs,
  FlightTextInput,
  FlightTooltip,
  getIcon,
} from '@flybits/design-system';
import { formatBytes } from 'helpers/common.helper';
import useDebounce from 'hooks/useDebounce';
import useCtxDataFiles, { CtxDataFilesScope, formatCompletedTimestamp } from 'hooks/useCtxDataFiles';
import { useAppSelector as useSelector } from 'hooks/reduxHooks';
import { ACTIVE_STATUSES } from 'store/journey/journey.type';
import { CtxDataFile } from 'interface/file/file.interface';
import { FILE_STATUS } from 'pages/ExperienceCanvas/types';
import LoadingIcon from 'components/Shared/LoadingIcon/LoadingIcon';
import RefreshIcon from '../assets/RefreshIcon';
import PauseIcon from '../assets/PauseIcon';
import UploadedStatusIcon from '../assets/UploadedStatusIcon';
import CompletedStatusIcon from '../assets/CompletedStatusIcon';
import ErrorStatusIcon from '../assets/ErrorStatusIcon';
import PausedStatusIcon from '../assets/PausedStatusIcon';
import PendingStatusIcon from '../assets/PendingStatusIcon';
import ProcessingStatusIcon from '../assets/ProcessingStatusIcon';
import { useFileActionRunner } from '../FileActionRunnerContext';
import {
  PROCESSING_QUEUED,
  STATUS_LABELS,
  STATUS_PRIORITY,
  StatusGroup,
  classifyStatus,
  isSelectableGroup,
  isTerminalFileStatus,
} from '../fileStatusGrouping';
import { HeaderActionSpec, resolveHeaderActions } from '../flowFileUploadHeaderActions';
import FlowFileUploadStatusBanner from '../FlowFileUploadStatusBanner/FlowFileUploadStatusBanner';
import './FlowFileUploadHistory.scss';

const MAIN_CLASS = 'flow-file-upload-history';

const CLASSES = {
  HEADER: `${MAIN_CLASS}__header`,
  TABS: `${MAIN_CLASS}__tabs`,
  TAB_PANEL: `${MAIN_CLASS}__tab-panel`,
  HEADER_CONTROLS: `${MAIN_CLASS}__header-controls`,
  ACTION_BTN: `${MAIN_CLASS}__action-btn`,
  ACTION_BTN_PRIMARY: `${MAIN_CLASS}__action-btn--primary`,
  ACTION_BTN_PAUSE: `${MAIN_CLASS}__action-btn--pause`,
  TABLE_WRAPPER: `${MAIN_CLASS}__table-wrapper`,
  STATUS_CELL: `${MAIN_CLASS}__status-cell`,
  STATUS_CELL_ERROR: `${MAIN_CLASS}__status-cell--error`,
  STATUS_LABEL: `${MAIN_CLASS}__status-label`,
  STATUS_PROCESSING_ICON: `${MAIN_CLASS}__status-processing-icon`,
  ERROR_TOGGLE: `${MAIN_CLASS}__error-toggle`,
  ERROR_TOGGLE_EXPANDED: `${MAIN_CLASS}__error-toggle--expanded`,
  ERROR_CONTAINER: `${MAIN_CLASS}__error-container`,
  FILENAME_HEADER: `${MAIN_CLASS}__filename-header`,
  FILENAME_CELL: `${MAIN_CLASS}__filename-cell`,
  CHECKBOX_SPACER: `${MAIN_CLASS}__checkbox-spacer`,
  // TODO: Restore when Context attributes / Related experiences columns ship.
  // RELATED_HEADER: `${MAIN_CLASS}__related-header`,
  EMPTY_STATE: `${MAIN_CLASS}__empty-state`,
  FOOTER_BAR: `${MAIN_CLASS}__footer-bar`,
  FOOTER_TIMER: `${MAIN_CLASS}__footer-timer`,
  FOOTER_REFRESH: `${MAIN_CLASS}__footer-refresh`,
  FOOTER_COMPLETED: `${MAIN_CLASS}__footer-completed`,
  FOOTER_ACTIONS: `${MAIN_CLASS}__footer-actions`,
  FOOTER_IN_NODE: `${MAIN_CLASS}__footer-bar--in-node`,
  FOOTER_SKIP: `${MAIN_CLASS}__footer-skip`,
  FOOTER_NODE_ACTIONS: `${MAIN_CLASS}__footer-node-actions`,
  FOOTER_EDIT_AUDIENCE: `${MAIN_CLASS}__footer-edit-audience`,
  FOOTER_COMPLETE_SETUP: `${MAIN_CLASS}__footer-complete-setup`,
};

const SORTABLE_HEADER_KEYS = new Set(['fileName', 'size', 'uploadedOn', 'status']);

const ERROR_ROW_KEY_SUFFIX = '__error';

const isFailedStatus = (status: string) =>
  status === FILE_STATUS.PROCESSING_FAILED || status === FILE_STATUS.UPLOADING_FAILED;

type FileTableRow = {
  key: string;
  fileName: React.ReactNode;
  size: React.ReactNode;
  uploadedOn: React.ReactNode;
  status: React.ReactNode;
  // TODO: Restore when Context attributes / Related experiences columns ship.
  // contextAttributes: React.ReactNode;
  // relatedExperiences: React.ReactNode;
};

const TABLE_STATUS_LABELS: Record<string, string> = {
  [FILE_STATUS.PROCESSING_SUCCESS]: 'Completed',
  [FILE_STATUS.PROCESSING]: 'Processing',
  [PROCESSING_QUEUED]: 'Pending',
  [FILE_STATUS.PROCESSING_PAUSED]: 'Paused',
  [FILE_STATUS.UPLOADED]: 'Uploaded',
  [FILE_STATUS.UPLOADING]: 'Processing',
  [FILE_STATUS.PROCESSING_FAILED]: 'Error',
  [FILE_STATUS.UPLOADING_FAILED]: 'Error',
};

// TODO: Restore when Related experiences column ships.
// const RELATED_EXPERIENCES_TOOLTIP = 'Experiences that use context attributes from this file.';

const STATUS_FILTER_OPTIONS = [
  { key: '', name: 'All statuses' },
  { key: FILE_STATUS.PROCESSING_FAILED, name: 'Failed' },
  { key: FILE_STATUS.PROCESSING_SUCCESS, name: 'Processed' },
];

type FileHistoryTab = 'ready' | 'history';

type FlowFileUploadHistoryProps = {
  scope?: CtxDataFilesScope;
  inNode?: boolean;
};

const FlowFileUploadHistory: React.FC<FlowFileUploadHistoryProps> = ({ scope = 'journey', inNode = false }) => {
  const [activeTab, setActiveTab] = useState<FileHistoryTab>('ready');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [activeStatusFilter, setActiveStatusFilter] = useState<string>('');
  const [sortByHeaderKey, setSortByHeaderKey] = useState<string>('uploadedOn');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
  const [expandedErrorFileIds, setExpandedErrorFileIds] = useState<Set<string>>(new Set());
  const [secondsAgo, setSecondsAgo] = useState(0);
  const lastFetchTimeRef = useRef<number>(Date.now());

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const journeyStatus = useSelector((state) => state.te.journey.status);
  // Inside an experience, processing a context file only means something once
  // that experience is live. A project-scoped page has no journey lifecycle to
  // wait on, so the actions are always available there.
  const canRunFileActions = scope === 'project' || ACTIVE_STATUSES.includes(journeyStatus);
  const { localFileStatuses, localFileErrors, isRunning, inFlightFileIds, setInFlightFileIds, runByIds } =
    useFileActionRunner();

  // Pull the whole file list for the active scope in a single request and
  // filter/sort/page entirely on the client. This avoids per-status count
  // requests and the "page 1 hides processed files" issue caused by
  // server-side sorting.
  const {
    isFetching,
    files: allFiles,
    counts,
    dataUpdatedAt,
    refetch,
  } = useCtxDataFiles({
    limit: 1000,
    sortby: 'createdAt',
    sortorder: 'desc',
    scope,
  });

  const getEffectiveStatus = useCallback(
    (file: CtxDataFile): string => {
      // Server-terminal statuses (success/failed) win over any stale optimistic
      // overlay so previously-completed files don't get pulled back into the
      // paused/processing buckets during pause/resume actions.
      if (isTerminalFileStatus(file.status)) {
        return file.status;
      }
      return localFileStatuses[file.id] ?? file.status;
    },
    [localFileStatuses],
  );

  // Upload History: Success and failed statuses.
  // Ready to Process: All other statuses.
  const isHistoryStatus = (status: string) => status === FILE_STATUS.PROCESSING_SUCCESS || isFailedStatus(status);

  const readyCount = useMemo(
    () => allFiles.filter((f) => getEffectiveStatus(f) === FILE_STATUS.UPLOADED).length,
    [allFiles, getEffectiveStatus],
  );

  const pendingCount = useMemo(
    () => allFiles.filter((f) => !isHistoryStatus(getEffectiveStatus(f))).length,
    [allFiles, getEffectiveStatus],
  );

  // Status pill → list of effective statuses that count as a match. The failed
  // pill bundles both failure statuses so the user doesn't have to think about
  // where the file broke.
  const statusFilterMatches = useCallback(
    (status: string): boolean => {
      if (!activeStatusFilter) return true;
      if (activeStatusFilter === FILE_STATUS.PROCESSING_FAILED) {
        return status === FILE_STATUS.PROCESSING_FAILED || status === FILE_STATUS.UPLOADING_FAILED;
      }
      return status === activeStatusFilter;
    },
    [activeStatusFilter],
  );

  const filteredFiles = useMemo(() => {
    const needle = debouncedSearchTerm.trim().toLowerCase();
    return allFiles.filter((f) => {
      const status = getEffectiveStatus(f);
      if (activeTab === 'ready') return !isHistoryStatus(status);
      if (!isHistoryStatus(status)) return false;
      if (needle && !f.name.toLowerCase().includes(needle)) return false;
      if (!statusFilterMatches(status)) return false;
      return true;
    });
  }, [activeTab, allFiles, debouncedSearchTerm, statusFilterMatches, getEffectiveStatus]);

  const sortedFiles = useMemo(() => {
    if (!SORTABLE_HEADER_KEYS.has(sortByHeaderKey)) return filteredFiles;
    const direction = sortOrder === 'asc' ? 1 : -1;
    const getValue = (file: CtxDataFile): string | number => {
      switch (sortByHeaderKey) {
        case 'fileName':
          return file.name.toLowerCase();
        case 'size':
          return file.size;
        case 'uploadedOn':
          return file.createdAt;
        case 'status':
          return getEffectiveStatus(file).toLowerCase();
        default:
          return 0;
      }
    };

    // Default sort path: priority group then uploadedOn desc.
    if (sortByHeaderKey === 'uploadedOn' && sortOrder === 'desc') {
      return [...filteredFiles].sort((a, b) => {
        const pa = STATUS_PRIORITY[getEffectiveStatus(a)] ?? 99;
        const pb = STATUS_PRIORITY[getEffectiveStatus(b)] ?? 99;
        if (pa !== pb) return pa - pb;
        return b.createdAt - a.createdAt;
      });
    }

    return [...filteredFiles].sort((a, b) => {
      const aVal = getValue(a);
      const bVal = getValue(b);
      if (aVal < bVal) return -1 * direction;
      if (aVal > bVal) return 1 * direction;
      return 0;
    });
  }, [filteredFiles, sortByHeaderKey, sortOrder, getEffectiveStatus]);

  const totalRecords = sortedFiles.length;
  const pageStart = (currentPage - 1) * perPage;
  const pagedFiles = useMemo(
    () => sortedFiles.slice(pageStart, pageStart + perPage),
    [sortedFiles, pageStart, perPage],
  );

  const selectablePagedFiles = useMemo(
    () =>
      activeTab === 'ready'
        ? pagedFiles.filter((file) => canRunFileActions && isSelectableGroup(classifyStatus(getEffectiveStatus(file))))
        : [],
    [activeTab, pagedFiles, canRunFileActions, getEffectiveStatus],
  );

  const headerCheckboxState = useMemo(() => {
    if (selectablePagedFiles.length === 0) return 'UNSELECTED';
    const selectedCount = selectablePagedFiles.filter((file) => selectedFileIds.has(file.id)).length;
    if (selectedCount === 0) return 'UNSELECTED';
    if (selectedCount === selectablePagedFiles.length) return 'SELECTED';
    return 'INDETERMINATE';
  }, [selectablePagedFiles, selectedFileIds]);

  const handleHeaderSort = (header: { key: string }) => {
    if (!SORTABLE_HEADER_KEYS.has(header.key)) return;
    if (header.key !== sortByHeaderKey) {
      setSortByHeaderKey(header.key);
      setSortOrder('desc');
    } else {
      setSortOrder((order) => (order === 'desc' ? 'asc' : 'desc'));
    }
  };

  useEffect(() => {
    if (dataUpdatedAt) {
      lastFetchTimeRef.current = dataUpdatedAt;
      setSecondsAgo(0);
    }
  }, [dataUpdatedAt]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, debouncedSearchTerm, perPage, activeStatusFilter]);

  // Clear the in-flight spinner once the server-truth status moves out of the
  // processing/queued bucket — i.e., the BE has actually advanced the file.
  useEffect(() => {
    setInFlightFileIds((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      allFiles.forEach((f) => {
        if (!next.has(f.id)) return;
        if (f.status !== FILE_STATUS.PROCESSING && f.status !== FILE_STATUS.PROCESSING_QUEUED) {
          next.delete(f.id);
        }
      });
      return next.size === prev.size ? prev : next;
    });
  }, [allFiles, setInFlightFileIds]);

  // Drop selections that are no longer on the current page so the action-bar
  // counts always reflect what the user can see.
  useEffect(() => {
    setSelectedFileIds((prev) => {
      if (prev.size === 0) return prev;
      const visibleIds = new Set(pagedFiles.map((f) => f.id));
      const next = new Set<string>();
      prev.forEach((id) => {
        if (visibleIds.has(id)) next.add(id);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [pagedFiles]);

  // Snap back to the last valid page if the active filter shrinks the result
  // set below the current page boundary.
  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(totalRecords / perPage));
    if (currentPage > lastPage) setCurrentPage(lastPage);
  }, [totalRecords, perPage, currentPage]);

  const completedTimestamp = useMemo(() => {
    if (!counts.allCompleted || !allFiles.length) return '';
    const latestCompletedAt = Math.max(...allFiles.map((f) => f.completedAt || 0));
    return latestCompletedAt > 0 ? formatCompletedTimestamp(latestCompletedAt) : '';
  }, [counts.allCompleted, allFiles]);

  // Group actions (pause-all / resume-all / process-all) target every file in
  // the journey for that status — not just the current page — so the action
  // buttons match the pill counts the user sees.
  const filesByGroup = useMemo(() => {
    const byGroup: Record<StatusGroup, CtxDataFile[]> = {
      processing: [],
      paused: [],
      ready: [],
      failed: [],
    };
    allFiles.forEach((f) => {
      const g = classifyStatus(getEffectiveStatus(f));
      if (g) byGroup[g].push(f);
    });
    return byGroup;
  }, [allFiles, getEffectiveStatus]);

  const selectedByGroup = useMemo(() => {
    const groups: Record<StatusGroup, string[]> = {
      processing: [],
      paused: [],
      ready: [],
      failed: [],
    };
    allFiles.forEach((f) => {
      if (!selectedFileIds.has(f.id)) return;
      const g = classifyStatus(getEffectiveStatus(f));
      if (g) groups[g].push(f.id);
    });
    return groups;
  }, [allFiles, selectedFileIds, getEffectiveStatus]);

  const showProcessingUi = counts.hasProcessing || isRunning;
  const allInFlightPaused = filesByGroup.processing.length === 0 && filesByGroup.paused.length > 0;
  const pausedQueueCount = filesByGroup.paused.length + filesByGroup.processing.length;
  const isDraftUploadedState = !canRunFileActions && allFiles.length > 0;
  const isReadyToProcessState =
    canRunFileActions && filesByGroup.ready.length > 0 && filesByGroup.ready.length === allFiles.length;

  useEffect(() => {
    if (!allFiles.length) return;
    const interval = setInterval(() => {
      setSecondsAgo(Math.floor((Date.now() - lastFetchTimeRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [allFiles.length]);

  const handleTabSelect = (tab: { key: string | number }) => {
    setActiveTab(tab.key.toString() as FileHistoryTab);
    setSelectedFileIds(new Set());
  };

  const toggleFileSelection = (fileId: string) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    if (isRunning || selectablePagedFiles.length === 0) return;
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      const allSelected = selectablePagedFiles.every((file) => next.has(file.id));
      selectablePagedFiles.forEach((file) => {
        if (allSelected) next.delete(file.id);
        else next.add(file.id);
      });
      return next;
    });
  };

  const toggleErrorDetails = (fileId: string) => {
    setExpandedErrorFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  };

  const dispatchAction = async (group: StatusGroup, fileIds: string[]) => {
    if (fileIds.length === 0) return;
    switch (group) {
      case 'processing':
        await runByIds({ action: 'PAUSE', fileIds, optimisticStatus: FILE_STATUS.PROCESSING_PAUSED });
        break;
      case 'paused':
        await runByIds({ action: 'RESUME', fileIds, optimisticStatus: FILE_STATUS.PROCESSING_QUEUED });
        break;
      case 'ready':
        await runByIds({ action: 'PROCESS', fileIds, optimisticStatus: FILE_STATUS.PROCESSING });
        break;
    }
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      fileIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  // A mixed selection (e.g. paused + ready) shows a single generic
  // "Process selected files" button, but each group must still hit its own
  // backend action: paused → RESUME, ready → PROCESS. Dispatch the
  // correct call per group while clearing the whole selection at the end.
  const dispatchSelectedFiles = async () => {
    const { processing: selProc, paused: selPaused, ready: selReady } = selectedByGroup;
    const processIds = selReady;
    if (selPaused.length > 0) {
      await runByIds({ action: 'RESUME', fileIds: selPaused, optimisticStatus: FILE_STATUS.PROCESSING_QUEUED });
    }
    if (processIds.length > 0) {
      await runByIds({ action: 'PROCESS', fileIds: processIds, optimisticStatus: FILE_STATUS.PROCESSING });
    }
    // selProc gets no backend call (those files are already processing); it's
    // only cleared so a currently-processing file doesn't linger in
    // selectedFileIds if it was selected earlier, before processing started.
    const handledIds = [...selPaused, ...processIds, ...selProc];
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      handledIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const handlePauseAll = (ids?: string[]) =>
    dispatchAction('processing', ids ?? filesByGroup.processing.map((f) => f.id));
  const handleResumeAll = (ids?: string[]) => dispatchAction('paused', ids ?? filesByGroup.paused.map((f) => f.id));
  const handleProcessAll = (ids?: string[]) => dispatchAction('ready', ids ?? filesByGroup.ready.map((f) => f.id));

  // TODO: Implement the node-flow navigation when the audience setup flow is finalized.
  const handleSkipForNow = () => null;

  // TODO: Open audience-rule editing when the audience setup flow is finalized.
  const handleEditAudienceRules = () => null;

  const renderFileNameCell = (file: CtxDataFile) => {
    const group = classifyStatus(getEffectiveStatus(file));
    const checkboxAllowed = activeTab === 'ready' && canRunFileActions && isSelectableGroup(group);

    if (checkboxAllowed) {
      return (
        <div className={CLASSES.FILENAME_CELL}>
          <FlightCheckbox
            checkState={selectedFileIds.has(file.id) ? 'SELECTED' : 'UNSELECTED'}
            onSelect={() => {
              if (isRunning) return;
              toggleFileSelection(file.id);
            }}
            disabled={isRunning}
          />
          <FlightTooltip description={file.name} direction="top">
            <span className={`${CLASSES.FILENAME_CELL}__name`}>{file.name}</span>
          </FlightTooltip>
        </div>
      );
    }
    return (
      <div className={CLASSES.FILENAME_CELL}>
        <span className={CLASSES.CHECKBOX_SPACER} aria-hidden />
        <FlightTooltip description={file.name} direction="top">
          <span className={`${CLASSES.FILENAME_CELL}__name`}>{file.name}</span>
        </FlightTooltip>
      </div>
    );
  };

  const renderStatusCell = (file: CtxDataFile) => {
    const status = getEffectiveStatus(file);
    const label = TABLE_STATUS_LABELS[status] ?? STATUS_LABELS[status];
    const localError = localFileErrors[file.id];
    const isInFlight = inFlightFileIds.has(file.id);

    const statusLabel = <span className={CLASSES.STATUS_LABEL}>{label}</span>;
    const processingIcon = <ProcessingStatusIcon className={CLASSES.STATUS_PROCESSING_ICON} ariaHidden={false} />;

    if (isInFlight) {
      return (
        <div className={CLASSES.STATUS_CELL} role="img" aria-label={label ?? 'Processing'}>
          {processingIcon}
          {statusLabel}
        </div>
      );
    }

    if (localError) {
      return (
        <div className={`${CLASSES.STATUS_CELL} ${CLASSES.STATUS_CELL_ERROR}`}>
          <FlightTooltip description={localError} direction="top">
            <ErrorStatusIcon ariaHidden={false} />
          </FlightTooltip>
          {statusLabel}
        </div>
      );
    }

    if (status === FILE_STATUS.PROCESSING_PAUSED) {
      return (
        <div className={CLASSES.STATUS_CELL} role="img" aria-label={label}>
          <PausedStatusIcon ariaHidden={false} />
          {statusLabel}
        </div>
      );
    }
    if (status === FILE_STATUS.PROCESSING_SUCCESS) {
      return (
        <div className={CLASSES.STATUS_CELL} role="img" aria-label={label}>
          <CompletedStatusIcon ariaHidden={false} />
          {statusLabel}
        </div>
      );
    }
    if (status === FILE_STATUS.PROCESSING || status === FILE_STATUS.UPLOADING) {
      return (
        <div className={CLASSES.STATUS_CELL} role="img" aria-label={label}>
          {processingIcon}
          {statusLabel}
        </div>
      );
    }
    if (status === FILE_STATUS.UPLOADED) {
      return (
        <div className={CLASSES.STATUS_CELL} role="img" aria-label={label}>
          <UploadedStatusIcon ariaHidden={false} />
          {statusLabel}
        </div>
      );
    }
    if (status === PROCESSING_QUEUED) {
      return (
        <div className={CLASSES.STATUS_CELL} role="img" aria-label={label}>
          <PendingStatusIcon ariaHidden={false} />
          {statusLabel}
        </div>
      );
    }
    if (isFailedStatus(status)) {
      const isExpanded = expandedErrorFileIds.has(file.id);
      return (
        <div className={`${CLASSES.STATUS_CELL} ${CLASSES.STATUS_CELL_ERROR}`}>
          <ErrorStatusIcon ariaHidden={false} />
          {statusLabel}
          <button
            type="button"
            className={`${CLASSES.ERROR_TOGGLE}${isExpanded ? ` ${CLASSES.ERROR_TOGGLE_EXPANDED}` : ''}`}
            aria-label={`Toggle error details for ${file.name}`}
            aria-expanded={isExpanded}
            aria-controls={`flow-file-error-panel-${file.id}`}
            onClick={(event) => {
              event.stopPropagation();
              toggleErrorDetails(file.id);
            }}
          >
            {getIcon('baselineKeyboardArrowDown', {})}
          </button>
        </div>
      );
    }
    return null;
  };

  const buildRow = (file: CtxDataFile): FileTableRow => ({
    key: file.fileID,
    fileName: renderFileNameCell(file),
    size: <div>{formatBytes(file.size)}</div>,
    uploadedOn: (
      <div title={moment(file.createdAt * 1000).format('MMMM, DD, YYYY, hh:mm:ss A')}>
        {moment(file.createdAt * 1000).format('MMM D,YYYY, h:mm:ssa')}
      </div>
    ),
    status: renderStatusCell(file),
    // TODO: Restore when Context attributes / Related experiences columns ship.
    // Need to replace with BE response.
    // contextAttributes: <div>—</div>,
    // relatedExperiences: <div>—</div>,
  });

  const buildErrorRow = (file: CtxDataFile): FileTableRow => {
    const status = getEffectiveStatus(file);
    return {
      key: `${file.fileID}${ERROR_ROW_KEY_SUFFIX}`,
      fileName: (
        <div className={CLASSES.ERROR_CONTAINER} id={`flow-file-error-panel-${file.id}`}>
          <p>
            {getIcon('infoFilled', {})}
            <span>
              {STATUS_LABELS[status]} - {moment(file.updatedAt * 1000).fromNow()}
            </span>
          </p>
          <p>{file.statusMessage}</p>
        </div>
      ),
      size: null,
      uploadedOn: null,
      status: null,
      // TODO: Restore when Context attributes / Related experiences columns ship.
      // contextAttributes: null,
      // relatedExperiences: null,
    };
  };

  const tableRows = pagedFiles.flatMap((file) => {
    const rows: FileTableRow[] = [buildRow(file)];
    if (isFailedStatus(getEffectiveStatus(file)) && expandedErrorFileIds.has(file.id)) {
      rows.push(buildErrorRow(file));
    }
    return rows;
  });

  const fileNameHeader = (
    <span className={CLASSES.FILENAME_HEADER}>
      {selectablePagedFiles.length > 0 ? (
        <FlightCheckbox
          checkState={headerCheckboxState}
          onSelect={(event) => {
            event.stopPropagation();
            toggleSelectAllOnPage();
          }}
          disabled={isRunning}
        />
      ) : (
        <span className={CLASSES.CHECKBOX_SPACER} aria-hidden />
      )}
      {'File Name'}
    </span>
  );

  const tableHeaders = [
    { name: fileNameHeader, key: 'fileName', isVisible: true, isSortable: true, hideTooltip: true },
    { name: 'Size', key: 'size', isVisible: true, isSortable: true, hideTooltip: true },
    { name: 'Uploaded on', key: 'uploadedOn', isVisible: true, isSortable: true, hideTooltip: true },
    { name: 'Status', key: 'status', isVisible: true, isSortable: true, hideTooltip: true },
    // TODO: Restore when Context attributes / Related experiences columns ship.
    // { name: 'Context attributes', key: 'contextAttributes', isVisible: true, isSortable: false, hideTooltip: true },
    // {
    //   name: (
    //     <span className={CLASSES.RELATED_HEADER}>
    //       Related experiences
    //       <FlightTooltip description={RELATED_EXPERIENCES_TOOLTIP} direction="top">
    //         {getIcon('infoStroke', { width: '16px', height: '16px', fill: '#82899b' })}
    //       </FlightTooltip>
    //     </span>
    //   ),
    //   key: 'relatedExperiences',
    //   isVisible: true,
    //   isSortable: false,
    //   hideTooltip: true,
    // },
  ];

  const paginationProps =
    totalRecords > perPage
      ? {
          totalPageNumber: Math.ceil(totalRecords / perPage),
          currentPageNumber: currentPage,
          currentRowsPerPage: perPage,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          handlePageChange: (page: any) => setCurrentPage(page),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          handleRowsPerPageChange: (rows: any) => setPerPage(rows),
        }
      : undefined;

  const executeHeaderAction = (spec: HeaderActionSpec) => {
    switch (spec.kind) {
      case 'pause-selected':
        return handlePauseAll(spec.fileIds);
      case 'resume-selected':
        return handleResumeAll(spec.fileIds);
      case 'process-selected':
        return handleProcessAll(spec.fileIds);
      case 'pause-all':
        return handlePauseAll();
      case 'resume-all':
        return handleResumeAll();
      case 'process-selected-mixed':
        return dispatchSelectedFiles();
      case 'process-all':
        return handleProcessAll();
      default:
        return undefined;
    }
  };

  const renderFooterActionButton = (spec: HeaderActionSpec, isDisabled = false) => {
    const button = (
      <button
        type="button"
        className={`${CLASSES.ACTION_BTN} ${
          spec.variant === 'pause' ? CLASSES.ACTION_BTN_PAUSE : CLASSES.ACTION_BTN_PRIMARY
        }`}
        onClick={() => executeHeaderAction(spec)}
        disabled={isRunning || isDisabled}
        aria-busy={spec.showLoadingWhenRunning && isRunning ? true : undefined}
      >
        <span>{spec.label}</span>
        {spec.showLoadingWhenRunning && isRunning ? (
          <LoadingIcon />
        ) : spec.icon === 'pause' ? (
          <PauseIcon ariaHidden />
        ) : null}
      </button>
    );

    if (spec.tooltip) {
      return (
        <FlightTooltip key={spec.key} description={spec.tooltip} direction="bottom">
          {button}
        </FlightTooltip>
      );
    }

    return <React.Fragment key={spec.key}>{button}</React.Fragment>;
  };

  const renderFooterAction = () => {
    if (activeTab !== 'ready' || !canRunFileActions) return null;

    if (selectedFileIds.size > 0) {
      const actions = resolveHeaderActions({
        canRunFileActions,
        hasSelection: true,
        showProcessingUi,
        selectedByGroup,
        filesByGroup,
        readyCount,
      });
      if (actions.length === 0) return null;
      return <>{actions.map((spec) => renderFooterActionButton(spec))}</>;
    }

    if (showProcessingUi) {
      return renderFooterActionButton({
        key: 'pause-all',
        kind: 'pause-all',
        label: 'Pause processing',
        variant: 'pause',
        icon: 'pause',
        tooltip: 'Pause processing',
      });
    }

    if (filesByGroup.paused.length > 0) {
      return renderFooterActionButton({
        key: 'resume-all',
        kind: 'resume-all',
        label: 'Resume processing',
        variant: 'primary',
        icon: 'play',
      });
    }

    return renderFooterActionButton(
      {
        key: 'process-selected-empty',
        kind: 'process-selected-mixed',
        label: 'Process selected files',
        variant: 'primary',
        icon: 'play',
      },
      true,
    );
  };

  const footerAction = renderFooterAction();
  const showFooterStatus = canRunFileActions && allFiles.length > 0;
  const getStatusBanner = () => {
    if (isDraftUploadedState) {
      return {
        tone: 'warning' as const,
        title: 'Launch this experience to unlock file processing.',
        description: 'Once you launch this experience, you will be able to process your uploaded files',
      };
    }
    if (canRunFileActions && showProcessingUi) {
      return {
        tone: 'info' as const,
        description: 'Processing one by one. You’re all set! Your files are queued and will start automatically',
      };
    }
    if (canRunFileActions && allInFlightPaused) {
      return {
        tone: 'warning' as const,
        title: 'Processing is currently paused.',
        description: `${pausedQueueCount} file${pausedQueueCount !== 1 ? 's' : ''} remain in the queue`,
      };
    }
    if (isReadyToProcessState) {
      return {
        tone: 'info' as const,
        description: 'Confirm your selection before processing. You can pause the process at any time',
      };
    }
    return null;
  };
  const statusBanner = getStatusBanner();
  const tableContent = (
    <div className={CLASSES.TABLE_WRAPPER}>
      {sortedFiles.length === 0 && !isFetching ? (
        <div className={CLASSES.EMPTY_STATE}>No files to show</div>
      ) : (
        <FlightTable
          tableHeaders={tableHeaders}
          tableData={tableRows}
          paginationProps={paginationProps}
          hasPaginationBeforeTable={false}
          hasPaginationAfterTable={totalRecords > perPage}
          handleHeaderSort={handleHeaderSort}
          sortByKey={sortByHeaderKey}
          sortOrder={sortOrder}
        />
      )}
    </div>
  );

  return (
    <div className={MAIN_CLASS}>
      <div className={CLASSES.HEADER}>
        <div className={CLASSES.TABS}>
          <FlightTabs
            labels={[
              { key: 'ready', name: `Ready to Process (${pendingCount})` },
              ...(!inNode ? [{ key: 'history', name: 'Upload history' }] : []),
            ]}
            selectedTab={activeTab}
            onSelect={handleTabSelect}
            orientation="left"
          />
        </div>
      </div>

      {activeTab === 'ready' && (
        <FlightTabPanel className={CLASSES.TAB_PANEL}>
          {statusBanner && <FlowFileUploadStatusBanner layout="inline" {...statusBanner} />}
          {tableContent}
        </FlightTabPanel>
      )}

      {activeTab === 'history' && (
        <FlightTabPanel className={CLASSES.TAB_PANEL}>
          <div className={CLASSES.HEADER_CONTROLS}>
            <FlightTextInput
              label="Search by file name"
              iconInput="search"
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            />
            <FlightSelect
              options={STATUS_FILTER_OPTIONS}
              label="Status"
              selected={STATUS_FILTER_OPTIONS.find((o) => o.key === activeStatusFilter) || STATUS_FILTER_OPTIONS[0]}
              handleOptionClick={(option: { key: string; name: string }) => setActiveStatusFilter(option.key)}
            />
          </div>
          {tableContent}
        </FlightTabPanel>
      )}

      {inNode ? (
        <div className={`${CLASSES.FOOTER_BAR} ${CLASSES.FOOTER_IN_NODE}`}>
          <button type="button" className={CLASSES.FOOTER_SKIP} onClick={handleSkipForNow}>
            Skip for now
          </button>
          <div className={CLASSES.FOOTER_NODE_ACTIONS}>
            <button type="button" className={CLASSES.FOOTER_EDIT_AUDIENCE} onClick={handleEditAudienceRules}>
              Edit audience rules
            </button>
            <button
              type="button"
              className={CLASSES.FOOTER_COMPLETE_SETUP}
              onClick={dispatchSelectedFiles}
              disabled={selectedFileIds.size === 0 || isRunning}
            >
              Complete Setup
            </button>
          </div>
        </div>
      ) : (
        (showFooterStatus || footerAction) && (
          <div className={CLASSES.FOOTER_BAR}>
            {showFooterStatus && (
              <>
                {counts.allCompleted && completedTimestamp ? (
                  <span className={CLASSES.FOOTER_COMPLETED}>
                    Completed: <span>{completedTimestamp}</span>
                  </span>
                ) : (
                  <span className={CLASSES.FOOTER_TIMER}>
                    Last updated: {secondsAgo} s ago
                  </span>
                )}
                <button
                  type="button"
                  className={CLASSES.FOOTER_REFRESH}
                  onClick={() => refetch()}
                  aria-label="Refresh now"
                  disabled={isFetching}
                >
                  Refresh now <RefreshIcon ariaHidden />
                </button>
              </>
            )}
            {footerAction && <div className={CLASSES.FOOTER_ACTIONS}>{footerAction}</div>}
          </div>
        )
      )}
    </div>
  );
};

export default FlowFileUploadHistory;
